'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { AgeGroup, Gender, StandardSize } from '@workspace/database';
import { toast } from '@workspace/ui/components/sonner';

import { getModelsVariantsForGeneratorAction } from '~/actions/fashionModels/get-models-variants-for-generator';
import {
  ModelVariantCard,
  SelectedModelVariantCard
} from '~/components/generator/cards/model-variant-cards';
import {
  ItemSelectionStep,
  type ItemSelectionStepProps
} from '~/components/generator/shared/item-selection-step';
import type { ItemSortOption } from '~/components/generator/shared/item-selection-step';
import { genderLabel } from '~/lib/labels';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';
import type { ModelVariantsDto } from '~/types/dtos/model-variant-dto';

const modelVariantSortOptions: ItemSortOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'size', label: 'Size' },
  { value: 'createdAt', label: 'Created Date' }
];

type GeneratorModelsVariantsStepProps =
  React.HtmlHTMLAttributes<HTMLDivElement> &
    ItemSelectionStepProps<ModelVariantsDto, {}>;

export function GeneratorModelsVariantsStep({
  ...props
}: Omit<
  GeneratorModelsVariantsStepProps,
  | 'fetcher'
  | 'onSelectionChange'
  | 'selectedIds'
  | 'ItemCardComponent'
  | 'SelectedItemCardComponent'
  | 'getFilterDefs'
  | 'title'
  | 'description'
  | 'maxSelection'
  | 'sortOptions'
  | 'handleItemSelect'
>): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();
  const selectedModelVariants = methods.watch('modelStep.modelVariants');
  const MAX_SELECTION = 4;
  const [fetchedModelsVariants, setFetchedModelsVariants] = React.useState<
    ModelVariantsDto[]
  >([]);
  const [genderGroup, setGenderGroup] = React.useState<Gender>(Gender.FEMALE);

  // Update selected model variants when fetched model variants change
  React.useEffect(() => {
    const currentSelectedModelVariants =
      methods.getValues('modelStep.modelVariants') || [];
    if (currentSelectedModelVariants.length > 0) {
      const updatedModelVariants = currentSelectedModelVariants.map(
        (selectedVariant) => {
          // If this is a fallback variant (model name is "Loading..."), try to find the real data
          if (selectedVariant.model?.name === 'Loading...') {
            const model = fetchedModelsVariants.find((m) =>
              m.variants?.some((variant) => variant.id === selectedVariant.id)
            );
            if (model?.variants) {
              const variant = model.variants.find(
                (v) => v.id === selectedVariant.id
              );
              if (variant) {
                return {
                  id: variant.id,
                  size: variant.size,
                  image: variant.image,
                  model: {
                    id: model.modelId,
                    name: model.modelName,
                    gender: model.gender,
                    ageGroup: model.ageGroup,
                    ethnicity: model.ethnicity,
                    image: model.image
                  }
                };
              }
            }
          }
          return selectedVariant;
        }
      );

      // Only update if there are changes
      const hasChanges = updatedModelVariants.some(
        (variant, index) =>
          variant.id !== currentSelectedModelVariants[index]?.id ||
          variant.model?.name !==
            currentSelectedModelVariants[index]?.model?.name
      );

      if (hasChanges) {
        methods.setValue('modelStep.modelVariants', updatedModelVariants, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [fetchedModelsVariants, methods]);

  const fetcher = React.useCallback(
    async (args: any) => {
      const result = await getModelsVariantsForGeneratorAction({
        pageIndex: args.pageIndex,
        pageSize: args.pageSize,
        sortBy: args.sortBy,
        sortDirection: args.sortDirection,
        searchQuery: args.searchQuery,
        // Group filter controls gender; override any inline filter selections
        genders: [genderGroup],
        ageGroups: args.filters.ageGroups ?? [],
        sizes: args.filters.sizes ?? []
      });
      if (result?.data && !result.serverError && !result.validationErrors) {
        const { modelsVariants, filteredCount, totalCount } = result.data;
        setFetchedModelsVariants((prev) => {
          const existingIds = new Set(prev.map((mv) => mv.id));
          const newModelsVariants = modelsVariants.filter(
            (mv) => !existingIds.has(mv.id)
          );
          return [...prev, ...newModelsVariants];
        });
        return {
          items: modelsVariants,
          filteredCount,
          totalCount
        };
      }

      return { items: [], filteredCount: 0, totalCount: 0 };
    },
    [genderGroup]
  );

  const onSelectionChange = React.useCallback(
    (selectedIds: string[]) => {
      const newModelVariants = selectedIds.map((id) => {
        // Find the model that contains this variant
        const model = fetchedModelsVariants.find((m) =>
          m.variants?.some((variant) => variant.id === id)
        );

        if (model?.variants) {
          // Find the concrete variant
          const variant = model.variants.find((v) => v.id === id);
          if (variant) {
            // Return a full ModelVariantDto shape
            return {
              id: variant.id,
              size: variant.size,
              image: variant.image,
              model: {
                id: model.modelId,
                name: model.modelName,
                gender: model.gender,
                ageGroup: model.ageGroup,
                ethnicity: model.ethnicity,
                image: model.image
              }
            };
          }
        }

        // Fallback: create a minimal model variant object if data not found
        // This prevents the error and allows the selection to work
        // The complete data will be fetched when needed
        console.warn(
          `Model variant data not immediately available for id: ${id}, using fallback`
        );
        return {
          id,
          size: StandardSize.M,
          image: {
            id: '',
            name: '',
            recordType: '',
            recordId: '',
            blobId: '',
            url: '',
            createdAt: new Date()
          },
          model: {
            id: '',
            name: 'Loading...',
            gender: Gender.FEMALE,
            ageGroup: AgeGroup.ADULT,
            ethnicity: null,
            image: {
              id: '',
              name: '',
              recordType: '',
              recordId: '',
              blobId: '',
              url: '',
              createdAt: new Date()
            }
          }
        };
      });

      methods.setValue('modelStep.modelVariants', newModelVariants, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    },
    [methods, fetchedModelsVariants]
  );

  const handleModelVariantSelect = React.useCallback(
    (item: ModelVariantsDto, selectedIds: string[]): string[] => {
      const isVariantAlreadySelected = selectedIds.includes(item.id);

      // Get all variant IDs for the selected item's model
      const allVariantIdsForThisModel = item.variants?.map((v) => v.id) ?? [];

      // Remove all variants of this model from the selection
      const selectionWithoutThisModel = selectedIds.filter(
        (id) => !allVariantIdsForThisModel.includes(id)
      );

      if (isVariantAlreadySelected) {
        // It was selected, so now it's deselected.
        // We already removed all variants for this model, so we're done.
        return selectionWithoutThisModel;
      } else {
        // It was not selected, so now we select it.
        // Check if adding this new model variant would exceed the max selection limit.
        if (selectionWithoutThisModel.length >= MAX_SELECTION) {
          toast.error(`Maximum ${MAX_SELECTION} items allowed`);
          return selectedIds; // Return original selection
        }

        // Add the new variant
        return [...selectionWithoutThisModel, item.id];
      }
    },
    []
  );

  const isItemSelected = React.useCallback(
    (item: ModelVariantsDto, selectedIds: string[]): boolean => {
      // A model is selected if any of its variants are in the selectedIds
      return (
        item.variants?.some((variant) => selectedIds.includes(variant.id)) ??
        false
      );
    },
    []
  );

  const getFilterDefs = React.useCallback(
    () => [
      {
        name: 'ageGroups',
        title: 'Age Group',
        options: [AgeGroup.ADULT, AgeGroup.SENIOR].map((ageGroup) => ({
          value: ageGroup,
          label: ageGroup
        }))
      },
      {
        name: 'sizes',
        title: 'Size',
        options: [
          StandardSize.S,
          StandardSize.M,
          StandardSize.L,
          StandardSize.XL
        ].map((size) => ({
          value: size,
          label: size
        }))
      }
    ],
    []
  );

  return (
    <ItemSelectionStep
      fetcher={fetcher}
      onSelectionChange={onSelectionChange}
      selectedIds={selectedModelVariants.map((variant) => variant.id) || []}
      ItemCardComponent={ModelVariantCard}
      SelectedItemCardComponent={
        SelectedModelVariantCard as unknown as React.ComponentType<{
          item: ModelVariantsDto;
          onRemove: (id: string) => void;
        }>
      }
      getFilterDefs={getFilterDefs}
      sortOptions={modelVariantSortOptions}
      isItemSelected={isItemSelected}
      handleItemSelect={handleModelVariantSelect}
      groupFilter={{
        options: [
          { value: Gender.FEMALE, label: genderLabel[Gender.FEMALE] },
          { value: Gender.MALE, label: genderLabel[Gender.MALE] }
        ],
        value: genderGroup,
        onValueChange: (value: string) => setGenderGroup(value as Gender),
        srLabel: 'Gender'
      }}
      title="Select your models"
      description="Select model variants to use in your generated images."
      searchPlaceholder="Search by model name..."
      maxSelection={MAX_SELECTION}
      {...props}
    />
  );
}
